import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import EmailVerificationService from '../../services/emailVerificationService';

const CheckEmailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Get email and password from location state (passed from RegisterPage)
  const { email, password } = location.state || {};

  const handleResend = async () => {
    if (!email || !password) {
      setError('Missing credentials. Please register again.');
      return;
    }

    try {
      setResending(true);
      setError('');
      setMessage('');

      await EmailVerificationService.resendVerificationEmail(email, password);
      setMessage('Verification email sent! Please check your inbox.');
    } catch (err: any) {
      setError(err.message || 'Failed to resend email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 3
            }}
          >
            <EmailIcon
              sx={{
                fontSize: 80,
                color: 'primary.main'
              }}
            />
          </Box>

          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Check Your Email
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We've sent a verification link to:
          </Typography>

          <Typography
            variant="h6"
            color="primary"
            sx={{
              mb: 3,
              fontWeight: 500,
              wordBreak: 'break-word'
            }}
          >
            {email || 'your email address'}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Click the link in the email to verify your account.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
            After verification, you can log in to your account.
          </Typography>

          {message && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {message}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleResend}
              disabled={resending || !email || !password}
              fullWidth
            >
              {resending ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Resending...
                </>
              ) : (
                'Resend Verification Email'
              )}
            </Button>

            <Button
              variant="contained"
              onClick={handleBackToLogin}
              fullWidth
            >
              Go to Login
            </Button>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 3 }}
          >
            Didn't receive the email? Check your spam folder or click resend.
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default CheckEmailPage;
